// Voice logging (premium): hold to record, release to transcribe + estimate.
// iOS WKWebView records audio/mp4 (AAC); everywhere else webm/opus — Whisper
// accepts both. Result opens FoodSheet pre-seeded with the estimate and the
// transcript editable for correction.
import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { hapticImpact, hapticSuccess } from "../lib/haptics";
import { SheetShell } from "./SheetShell";
import { Paywall } from "./Paywall";
import type { EstimateResponse } from "@shared/types";

const MAX_SECONDS = 60;

interface Props {
  onResult: (estimate: EstimateResponse, transcript: string) => void;
  onClose: () => void;
}

type Phase = "idle" | "recording" | "transcribing" | "denied" | "error";

export function VoiceLogSheet({ onResult, onClose }: Props) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recRef.current?.stream.getTracks().forEach((t_) => t_.stop());
  }, []);

  async function start() {
    if (phase === "recording") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => { void submit(rec.mimeType); };
      rec.start();
      recRef.current = rec;
      hapticImpact();
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      setPhase("denied");
    }
  }

  function stop() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
      rec.stream.getTracks().forEach((t_) => t_.stop());
    }
  }

  async function submit(mime: string) {
    setPhase("transcribing");
    try {
      const blob = new Blob(chunksRef.current, { type: mime });
      if (blob.size < 1000) { setPhase("idle"); return; } // accidental tap
      const buf = await blob.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      const b64 = btoa(bin);
      const r = await api.estimateVoice(b64, mime);
      hapticSuccess();
      onResult(r, r.transcript);
    } catch (e) {
      if (e instanceof ApiError && (e.message === "ai_limit_reached" || e.message === "premium_required")) setShowPaywall(true);
      else setPhase("error");
      return;
    }
  }

  return (
    <SheetShell onClose={() => { stop(); onClose(); }}>
      <h3 className="text-lg font-semibold mt-4 mb-1">{t("log.voice")}</h3>
      <p className="text-sm text-fg-muted mb-6">
        {phase === "recording" ? t("voice.recording", { s: String(seconds) })
          : phase === "transcribing" ? t("voice.transcribing")
          : phase === "denied" ? t("voice.mic_denied")
          : phase === "error" ? t("voice.failed")
          : t("voice.hold_hint")}
      </p>

      <div className="flex justify-center mb-6">
        <button
          type="button"
          onPointerDown={(e) => { e.preventDefault(); void start(); }}
          onPointerUp={() => stop()}
          onPointerLeave={() => { if (phase === "recording") stop(); }}
          disabled={phase === "transcribing"}
          aria-label={t("log.voice")}
          className={`w-24 h-24 rounded-full flex items-center justify-center transition-transform ${
            phase === "recording" ? "bg-danger scale-110" : "bg-brand"
          } text-on-brand disabled:opacity-50`}
          style={{ touchAction: "none" }}
        >
          <Mic size={40} aria-hidden="true" />
        </button>
      </div>
      <div className="pb-4" />
      {showPaywall && <Paywall trigger="feature" onClose={() => { setShowPaywall(false); onClose(); }} />}
    </SheetShell>
  );
}
